import { nieuweSleutel } from './PuttenPaneel';

/* Vertaling tussen het puttenmodel van PuttenPaneel en wat de backend stuurt
   en verwacht. Eén plek, zodat alle drie de schermen straks dezelfde vertaling
   gebruiken en er geen drie varianten meer ontstaan.

   ── Waarom een bijlage geen matchvraag oplevert ───────────────────────────

   Een melding is een eenzijdige rechtshandeling; het waterschap mag de inhoud
   ervan niet wijzigen. Beslissen dat de melder "eigenlijk" een put op 1,2 meter
   afstand bedoelde is precies dat. Bovendien is de vraag verzonnen: wie in het
   meldingsformulier een bestaande put opgeeft, kiest die uit het register, en
   dan zijn de gemelde coördinaten die van de registerput zelf.

   Vandaar: uit een bijlage koppelen we alleen bij een exacte treffer — dezelfde
   hele meter. Dat is geen interpretatie maar een gelijkheid. Alle andere putten
   houden de soort die de melder zelf opgaf. */

const rond = (v) => (v == null ? null : Math.round(Number(v)));

/* De registerput die exact op de gemelde plek ligt. De backend levert per
   gemelde put alle registerputten binnen vijf meter; hieruit halen we alleen
   degene die op dezelfde hele meter ligt. */
function exactUitMatches(pm) {
  return (pm.matches || []).find(
    (m) => rond(m.x_rd) === rond(pm.x_rd) && rond(m.y_rd) === rond(pm.y_rd)
      && koppelbaar(m)
  ) || null;
}

/* Aan een put die eruit gaat hangen we niets. De backend levert de toestand
   mee; alles behalve 'koppelbaar' is voor het scherm om te tonen, niet om te
   kiezen. Oude antwoorden zonder toestand gelden als koppelbaar, zodat een
   backend van vóór deze wijziging niet ineens alles blokkeert. */
export const koppelbaar = (m) => !m.toestand || m.toestand === 'koppelbaar';

/* De registerput die in de weg zit. Alleen als de melder zegt dat de put er al
   ligt: zegt hij dat hij nog geboord moet worden, dan is een afgedichte put op
   diezelfde plek geen tegenspraak maar een opvolger.

   `exact` betekent: op dezelfde hele meter. Dat is de put waar de melding aan
   gekoppeld zou zijn als hij niet geblokkeerd was — en dus de put die na het
   intrekken van de procedure alsnog de juiste is. */
export function blokkadeUitMatches(pm) {
  if (pm.bron_volgens_melder === 'nieuw') return null;
  const gehinderd = (pm.matches || []).filter((m) => !koppelbaar(m));
  if (gehinderd.length === 0) return null;
  const dichtstbij = gehinderd.reduce((a, b) =>
    (b.afstand_meter < a.afstand_meter ? b : a));
  return {
    put_id: dichtstbij.put_id,
    toestand: dichtstbij.toestand,
    afstand_meter: dichtstbij.afstand_meter,
    afdichting_gestart_datum: dichtstbij.afdichting_gestart_datum || null,
    buiten_gebruik_datum: dichtstbij.buiten_gebruik_datum || null,
    exact: rond(dichtstbij.x_rd) === rond(pm.x_rd) && rond(dichtstbij.y_rd) === rond(pm.y_rd),
  };
}

// preview van /import-melding/preview → het gedeelde puttenmodel.
export function puttenUitPreview(preview) {
  return (preview.put_matches || []).map((pm) => {
    const exact = exactUitMatches(pm);
    const bestaandVolgensMelder = pm.bron_volgens_melder === 'bestaand';
    return {
      sleutel: nieuweSleutel(),
      bronId: pm.pdf_id,
      soort: exact ? 'gekoppeld' : (bestaandVolgensMelder ? 'ongeregistreerd' : 'nieuw'),
      put_id: exact ? exact.put_id : null,
      // Komt uit het register: de soort ligt vast, daar valt niets aan te kiezen.
      uitRegister: !!exact,
      x_rd: pm.x_rd, y_rd: pm.y_rd,
      perceel: pm.perceel || null,
      max_diepte_mv: pm.max_diepte_mv_uit_pdf != null ? String(pm.max_diepte_mv_uit_pdf) : '',
      opgegeven_diepte_m: pm.opgegeven_diepte_m_uit_pdf != null ? String(pm.opgegeven_diepte_m_uit_pdf) : '',
      /* Wat de melder over de boorstatus zei ('geboord' / 'te_boren'), en wat
         het register erover weet als de put daar al staat. De kolom in het
         paneel toont het eerste en zet het tweede eronder als het afwijkt;
         zie boorstatusVan in PuttenPaneel. */
      boorstatus: pm.boorstatus_volgens_melder || null,
      registerStatus: exact ? (exact.boorstatus_code || null) : null,
      registerDiepte: exact ? exact.opgegeven_diepte_m ?? null : null,
      // Bewust leeg: uit een bijlage valt er niets te kiezen.
      kandidaten: exact ? [{ ...exact, afstand_meter: 0 }] : [],
      blokkade: blokkadeUitMatches(pm),
      /* Het besluitnummer komt uit het register, nooit uit de bijlage: de
         melder geeft het niet op. Alleen bij een exacte treffer weten we om
         welke registerput het gaat. */
      besluitnummer: exact && exact.besluitnummer_wsw ? exact.besluitnummer_wsw : '',
      herkomst: 'pdf',
    };
  });
}

// De melding-kop uit de preview → de formulierwaarden.
export function waardenUitPreview(preview) {
  const m = preview.melding || {};
  return {
    kenmerkType: 'verzoeknummer',
    kenmerk: m.verzoeknummer || '',
    meldingsdatum: null,
    doel: m.doel || '',
    pompcapaciteit: m.maxPompcapaciteitM3PerUur != null ? String(m.maxPompcapaciteitM3PerUur) : '',
    hectare: m.bedrijfsomvangHa != null ? String(m.bedrijfsomvangHa) : '',
  };
}

// De actieve putten van een bestaande inrichting → het puttenmodel.
export function puttenUitInrichting(actievePutten) {
  return (actievePutten || []).map((pp) => ({
    sleutel: nieuweSleutel(),
    bronId: null,
    soort: 'gekoppeld',
    put_id: pp.put_id,
    uitRegister: true,
    x_rd: pp.x_rd != null ? Number(pp.x_rd) : null,
    y_rd: pp.y_rd != null ? Number(pp.y_rd) : null,
    perceel: pp.perceel || null,
    max_diepte_mv: pp.max_diepte_mv != null ? String(pp.max_diepte_mv) : '',
    opgegeven_diepte_m: pp.opgegeven_diepte_m != null ? String(pp.opgegeven_diepte_m) : '',
    boorstatus: null,
    registerStatus: pp.boorstatus_code || null,
    registerDiepte: pp.opgegeven_diepte_m ?? null,
    besluitnummer: pp.besluitnummer_wsw || '',
    kandidaten: [],
    herkomst: 'overgenomen',
  }));
}

/* De payload van een klaarstaande melding terug naar het formulier.

   Spiegelbeeld van bouwImportPayload hieronder. Een klaarstaande melding is nog
   niet verwerkt, dus er is niets uit het register te halen: alles wat je ziet
   komt uit de bewaarde payload zelf. */
export function waardenUitPayload(payload) {
  const m = (payload?.melding_data || {}).melding || {};
  return {
    kenmerkType: payload?.kenmerk_type || 'verzoeknummer',
    kenmerk: payload?.verzoeknummer || '',
    meldingsdatum: payload?.meldingsdatum || '',
    doel: m.doel || '',
    pompcapaciteit: m.maxPompcapaciteitM3PerUur != null ? String(m.maxPompcapaciteitM3PerUur) : '',
    hectare: m.bedrijfsomvangHa != null ? String(m.bedrijfsomvangHa) : '',
  };
}

export function puttenUitPayload(payload) {
  const lijst = ((payload?.melding_data || {}).putten || {}).lijst || [];
  const keuzes = {};
  (payload?.put_keuzes || []).forEach((k) => { keuzes[k.pdf_index] = k; });
  return lijst.map((p) => {
    const keuze = keuzes[p.id] || {};
    const put_id = keuze.bestaande_put_id || null;
    const max = p.maxBoordiepteNapM;
    /* Een te boren put uit een klaarstaande melding heeft sinds het klaarzetten
       een putnummer. Dat maakt hem nog geen registerput uit een eerder recht:
       hij is er juist dóór deze melding. Dus blijft de soort volgen wat de
       melder opgaf, en reist het nummer los mee zodat het bij het opslaan aan
       dezelfde rij blijft hangen in plaats van een tweede put te maken. */
    return {
      sleutel: nieuweSleutel(),
      bronId: p.id,
      soort: p.bron === 'nieuw' ? 'nieuw' : (put_id ? 'gekoppeld' : 'ongeregistreerd'),
      put_id,
      uitRegister: p.bron !== 'nieuw' && !!put_id,
      x_rd: p.rdCoordinaten?.x ?? null,
      y_rd: p.rdCoordinaten?.y ?? null,
      perceel: p.perceel || null,
      // De payload bewaart de diepte onder maaiveld als negatief getal.
      max_diepte_mv: max == null ? '' : String(Math.abs(max)),
      // Nieuwe sleutel sinds migratie 47; oudere payloads hebben de oude.
      opgegeven_diepte_m: (p.opgegevenDiepteM ?? p.werkelijkeDiepteM) != null
        ? String(p.opgegevenDiepteM ?? p.werkelijkeDiepteM) : '',
      boorstatus: p.boorstatus || null,
      /* Wat er in het register stond voordat deze melding werd klaargezet;
         de backend bewaart dat in de keuze om het bij intrekken terug te
         kunnen zetten. Hier is het de "was:"-regel. */
      registerStatus: keuze.vorige_registerput
        ? (keuze.vorige_registerput.nog_te_boren ? 'te_boren'
          : (keuze.vorige_registerput.geboord_bevestigd_op ? 'geboord' : 'onbekend'))
        : null,
      registerDiepte: keuze.vorige_registerput ? keuze.vorige_registerput.opgegeven_diepte_m ?? null : null,
      besluitnummer: keuze.besluitnummer_wsw || '',
      kandidaten: [],
      herkomst: 'klaarstaand',
    };
  });
}

const getal = (v) => {
  if (v === '' || v == null) return null;
  const n = parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/* Het puttenmodel → de body van POST /import-melding.

   `pdf_index` is de sleutel waarmee de backend een put-keuze aan een put in de
   lijst koppelt. Voor putten uit een bijlage is dat het id uit de PDF, voor
   handmatig opgevoerde putten een eigen volgnummer. */
export function bouwImportPayload({ houderId, waarden, meldingsdatum, putten }) {
  const genummerd = putten.map((p, i) => ({ ...p, _idx: p.bronId != null ? p.bronId : `manual-${i + 1}` }));
  return {
    houder_id: houderId,
    verzoeknummer: String(waarden.kenmerk || '').trim(),
    ingangsdatum: meldingsdatum,
    kenmerk_type: waarden.kenmerkType,
    meldingsdatum,
    rechtstype: 'melding',
    put_keuzes: genummerd.map((p) => ({
      pdf_index: p._idx,
      /* Het putnummer, ongeacht de soort. Een te boren put uit de wachtkamer
         heeft er al een; zonder dit veld zou hij bij elke keer opslaan opnieuw
         worden aangemaakt. */
      bestaande_put_id: p.put_id || null,
      /* Besluitnummer van de vergunning voor het waterstaatswerk waarin de put
         ligt. Hoort bij de keuze van de medewerker en niet bij de melding, dus
         niet in melding_data: de melder geeft dit niet op. Leeg = niet
         opgegeven; de backend laat een al vastgelegd nummer dan staan. */
      besluitnummer_wsw: String(p.besluitnummer || '').trim() || null,
    })),
    melding_data: {
      melding: {
        doel: waarden.doel || null,
        maxPompcapaciteitM3PerUur: getal(waarden.pompcapaciteit),
        bedrijfsomvangHa: getal(waarden.hectare),
      },
      putten: {
        lijst: genummerd.map((p) => {
          /* De backend leest maxBoordiepteNapM en doet daar abs() overheen; wij
             sturen de diepte onder maaiveld dus als negatief getal. Let op de
             expliciete null-check: met een truthy-test werd een diepte van 0
             stilletjes null. */
          const max = getal(p.max_diepte_mv);
          const opgegeven = p.soort === 'nieuw' ? null : getal(p.opgegeven_diepte_m);
          /* Wat de melder (of, bij een papieren melding, de behandelaar namens
             hem) over de boorstatus zegt. Een te boren put is dat per definitie,
             een geboorde put niet in het register ook; bij een registerput
             alleen als er iets is gekozen -- zonder keuze laat de inlees de put
             met rust. */
          const boorstatus = p.soort === 'nieuw' ? 'te_boren'
            : p.soort === 'ongeregistreerd' ? 'geboord'
            : (p.boorstatus || null);
          return {
            id: p._idx,
            /* Dezelfde sleutel als in de bijlage van de melder: 'nieuw' of
               'bestaand'. De backend leest 'm bij een PDF ook zo, en wij hebben
               'm nodig om een klaarstaande melding weer te kunnen openen —
               zonder dit veld is een gerealiseerde put zonder ingevulde diepte
               niet te onderscheiden van een te boren put. */
            bron: p.soort === 'nieuw' ? 'nieuw' : 'bestaand',
            rdCoordinaten: { x: getal(p.x_rd), y: getal(p.y_rd) },
            perceel: p.perceel || null,
            maxBoordiepteNapM: max == null ? null : -max,
            opgegevenDiepteM: opgegeven,
            // Eén versie ook onder de oude naam, voor lezers die nog niet om zijn.
            werkelijkeDiepteM: opgegeven,
            ...(boorstatus ? { boorstatus } : {}),
          };
        }),
      },
    },
  };
}
